/**
 * 标签选择器组件
 *
 * 用于创建工作区时选择标签，支持：
 * - 直接展示当前流派下的全部标签
 * - 自定义标签输入
 * - 最多选择3个标签
 */

import React, { useState, useEffect, useCallback } from 'react';
import { tagApi } from '../../services/tagApi';
import type { TagWithWeight } from '../../types/tag';

interface TagSelectorProps {
  /** 分类 */
  category: 'male' | 'female';
  /** 流派 */
  genre: string;
  /** 已选择的标签ID列表 */
  selectedTagIds: number[];
  /** 标签变更回调 */
  onChange: (tagIds: number[]) => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 最大选择数量（固定为3） */
  maxSelections?: number;
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  category,
  genre,
  selectedTagIds,
  onChange,
  disabled = false,
  maxSelections = 3,
}) => {
  const [tags, setTags] = useState<TagWithWeight[]>([]);
  const [loading, setLoading] = useState(false);
  const [customTagInput, setCustomTagInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  // 加载标签列表
  useEffect(() => {
    const fetchTags = async () => {
      setLoading(true);
      try {
        const tagList = await tagApi.getTagsByGenre(category, genre);
        setTags(tagList);
      } catch (error) {
        console.error('加载标签失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, [category, genre]);

  // 切换标签选择
  const handleToggleTag = useCallback((tagId: number) => {
    if (disabled) return;

    const newSelection = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : selectedTagIds.length >= maxSelections
        ? selectedTagIds
        : [...selectedTagIds, tagId];

    onChange(newSelection);
  }, [selectedTagIds, onChange, disabled, maxSelections]);

  // 添加自定义标签
  const handleAddCustomTag = useCallback(async () => {
    if (!customTagInput.trim() || disabled) return;

    // 检查是否已存在
    const existingTag = tags.find((t) => t.name === customTagInput.trim());
    if (existingTag) {
      handleToggleTag(existingTag.id);
      setCustomTagInput('');
      return;
    }

    try {
      setLoading(true);
      const newTag = await tagApi.createCustomTag({ name: customTagInput.trim() });
      setTags((prev) => [...prev, { ...newTag, weight: 100 }]);
      handleToggleTag(newTag.id);
      setCustomTagInput('');
    } catch (error) {
      console.error('创建标签失败:', error);
      setError(error instanceof Error ? error.message : '创建标签失败');
    } finally {
      setLoading(false);
    }
  }, [customTagInput, tags, handleToggleTag, disabled]);

  if (loading && tags.length === 0) {
    return (
      <div className="p-4 text-center text-gray-600">
        加载标签列表...
      </div>
    );
  }

  const renderTagItem = (tag: TagWithWeight) => {
    const isSelected = selectedTagIds.includes(tag.id);

    return (
      <button
        key={tag.id}
        type="button"
        onClick={() => handleToggleTag(tag.id)}
        disabled={disabled}
        className={`
          px-3 py-1.5 rounded-full text-sm border transition-all
          ${disabled
            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
            : isSelected
              ? 'bg-ink-800 border-paper-500 text-white'
              : 'bg-white border-gray-300 text-gray-700 hover:border-paper-400 hover:bg-paper-50'
          }
        `}
      >
        {tag.name}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {/* 头部 */}
      <label className="block text-sm font-medium text-gray-900">
        小说标签
        <span className="text-gray-600 font-normal ml-2">
          (已选择 {selectedTagIds.length}/{maxSelections})
        </span>
      </label>

      {/* 错误提示 */}
      {error && (
        <div className="text-sm text-red-600">
          {error}
        </div>
      )}

      {/* 自定义标签输入 */}
      <div className="flex gap-2">
        <input
          type="text"
          value={customTagInput}
          onChange={(e) => setCustomTagInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddCustomTag()}
          placeholder="输入自定义标签..."
          disabled={disabled}
          className="flex-1 px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500"
        />
        <button
          type="button"
          onClick={handleAddCustomTag}
          disabled={disabled || !customTagInput.trim()}
          className="px-4 py-2 bg-ink-800 hover:bg-ink-700 text-white rounded-md transition-colors disabled:opacity-50"
        >
          添加
        </button>
      </div>

      {/* 标签列表 */}
      <div className="flex flex-wrap gap-2">
        {tags.map(renderTagItem)}
      </div>
    </div>
  );
};

export default TagSelector;
